<script setup>
/**
 * pages/attendance/schedules.vue — Addendum 08, Modul Z
 * ─────────────────────────────────────────────────────────
 * Grid 7 hari x jam masuk/keluar per staff. 1 staff dipilih dari dropdown,
 * grid mingguannya di-load, diedit, lalu disimpan sekaligus (1 PUT untuk
 * seluruh minggu — lihat StaffScheduleService::saveWeek()).
 *
 * Staff tanpa jadwal ter-set sama sekali (baru pertama kali dibuka) tetap
 * tampil normal — grid diisi default "belum diatur" (is_working_day=true,
 * jam kosong), BUKAN error. Menyimpan jadwal kosong (jam belum diisi) untuk
 * hari kerja ditolak validasi backend (StaffScheduleRequest), sama seperti
 * form lain di project ini.
 */
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Attendance',
  },
})

const ability = useAbility()
const { errors, request } = useApiRequest()

// 0=Minggu..6=Sabtu — SAMA konvensi dengan pages/promotions/index.vue
// (Promotion.active_days) & Carbon::dayOfWeek di backend.
const dayLabels = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', "Jum'at", 'Sabtu']

const defaultWeek = () => dayLabels.map((label, dayOfWeek) => ({
  day_of_week: dayOfWeek,
  label,
  is_working_day: true,
  expected_clock_in: null,
  expected_clock_out: null,
}))

// ── Staff picker ───────────────────────────────────────────────────────
const staffList = ref([])
const selectedStaffId = ref(null)
const loadingStaff = ref(false)

const fetchStaffList = async () => {
  loadingStaff.value = true
  try {
    const response = await useApi('/api/v1/users', { query: { per_page: 100, is_active: 1 } }).get()
    const data = response?.data ?? response

    // /api/v1/users TIDAK dibungkus ApiResponse (beda dari kebanyakan endpoint
    // list lain di app ini) -- balikannya langsung shape default Laravel resource
    // collection {data:[...], links, meta} (SATU level, bukan {data:{data:[...]}}
    // seperti endpoint yang lewat ApiResponse::paginated()). data?.data di sini
    // dulu SELALU undefined (data sudah berupa array), makanya dropdown selalu
    // kosong walau staff-nya ada. Pola defensif ini sama dengan yang sudah benar
    // di apps/user/list/index.vue::fetchUsers() untuk endpoint yang sama.
    staffList.value = Array.isArray(data) ? data : (data?.data ?? [])
  } finally {
    loadingStaff.value = false
  }
}

// ── Grid mingguan ──────────────────────────────────────────────────────
const week = ref(defaultWeek())
const loadingWeek = ref(false)
const saving = ref(false)

const fetchWeek = async () => {
  if (!selectedStaffId.value) return

  loadingWeek.value = true
  try {
    const response = await useApi(`/api/v1/staff-schedules/${selectedStaffId.value}`).get()
    const data = response?.data ?? response ?? []

    const byDay = Object.fromEntries(data.map(row => [row.day_of_week, row]))

    week.value = defaultWeek().map(day => {
      const existing = byDay[day.day_of_week]

      return existing
        ? { ...day, is_working_day: existing.is_working_day, expected_clock_in: existing.expected_clock_in, expected_clock_out: existing.expected_clock_out }
        : day
    })
  } finally {
    loadingWeek.value = false
  }
}

watch(selectedStaffId, fetchWeek)

const errorFor = index => errors.value?.[`days.${index}.expected_clock_in`]?.[0]

const canManage = computed(() => ability.can('manage', 'Attendance'))

const saveWeek = async () => {
  saving.value = true
  try {
    const payload = {
      days: week.value.map(day => ({
        day_of_week: day.day_of_week,
        is_working_day: day.is_working_day,
        expected_clock_in: day.is_working_day ? day.expected_clock_in : null,
        expected_clock_out: day.expected_clock_out || null,
      })),
    }

    await request(() => useApi(`/api/v1/staff-schedules/${selectedStaffId.value}`).put({ body: payload }))
  } finally {
    saving.value = false
  }
}

onMounted(fetchStaffList)
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">
          Jadwal Kerja Staff
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Atur jam masuk/keluar per hari untuk tiap staff — dipakai menghitung keterlambatan absensi.
        </p>
      </VCol>
    </VRow>

    <VCard>
      <VCardText>
        <VAutocomplete
          v-model="selectedStaffId"
          :items="staffList"
          item-title="name"
          item-value="id"
          label="Pilih Staff"
          placeholder="Cari nama staff..."
          prepend-inner-icon="ri-user-search-line"
          :loading="loadingStaff"
          clearable
          style="max-width: 360px"
        />
      </VCardText>

      <VDivider v-if="selectedStaffId" />

      <VCardText v-if="selectedStaffId">
        <VTable v-if="!loadingWeek">
          <thead>
            <tr>
              <th>Hari</th>
              <th style="width: 130px">
                Hari Kerja
              </th>
              <th>Jam Masuk</th>
              <th>Jam Pulang</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(day, index) in week"
              :key="day.day_of_week"
            >
              <td class="font-weight-medium">
                {{ day.label }}
              </td>
              <td>
                <VSwitch
                  v-model="day.is_working_day"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!canManage"
                />
              </td>
              <td>
                <VTextField
                  v-model="day.expected_clock_in"
                  type="time"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :disabled="!canManage || !day.is_working_day"
                  :error-messages="errorFor(index)"
                  style="max-width: 160px"
                />
              </td>
              <td>
                <VTextField
                  v-model="day.expected_clock_out"
                  type="time"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :disabled="!canManage || !day.is_working_day"
                  style="max-width: 160px"
                />
              </td>
            </tr>
          </tbody>
        </VTable>

        <div
          v-else
          class="d-flex justify-center pa-8"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </div>

        <div
          v-if="ability.can('manage', 'Attendance')"
          class="d-flex justify-end mt-4"
        >
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveWeek"
          >
            Simpan Jadwal
          </VBtn>
        </div>
      </VCardText>

      <VCardText
        v-else
        class="text-center text-medium-emphasis py-8"
      >
        Pilih staff untuk melihat/atur jadwal kerjanya.
      </VCardText>
    </VCard>
  </div>
</template>
